// SPDX-License-Identifier: AGPL-3.0-or-later
// Events with an expected cost are money already spoken for. Until a matching
// transaction turns up they count against the budget as "scheduled"; once it
// does, the transaction counts instead, so nothing is counted twice.
import { addDays, daysBetween, type Cents, type IsoDate } from "@almanac/domain";
import { FRONTED, NOT_SPENDING, mainOf, within } from "./categories.js";
import { agenda } from "./events.js";
import type { CalendarEvent, Transaction } from "./model.js";

/** How far a payment's date can be from the event's and still count as paying it. */
export const MATCH_DAYS = 5;
/** How far a payment's amount can be from the event's, as a share of it. */
export const MATCH_SHARE = 0.1;

export interface Commitment {
  event: CalendarEvent;
  date: IsoDate;
  amount: Cents;
  /** The event's category, or "other" when it has none. */
  category: string;
  /** The transaction that paid it, once one turns up. */
  paidBy: Transaction | null;
}

const related = (a: string, b: string) => within(a, b) || within(b, a);

/**
 * Occurrences between `start` and `end` of events that cost money from the
 * spending budget, each paired with the transaction that paid it, if any. A
 * payment is a transaction in a related category (the same one, or one
 * above or below it), within 10% of the amount and 5 days of the date. Each
 * transaction pays at most one occurrence, closest date first.
 */
export function commitments(events: readonly CalendarEvent[], transactions: readonly Transaction[], start: IsoDate, end: IsoDate): Commitment[] {
  const list: Commitment[] = agenda(events, start, end)
    .filter((o) => (o.event.amount ?? 0) > 0 && !NOT_SPENDING.has(mainOf(o.event.category ?? "other")))
    .map((o) => ({ event: o.event, date: o.date, amount: o.event.amount!, category: o.event.category ?? "other", paidBy: null }));
  if (list.length === 0) return list;
  const from = addDays(start, -MATCH_DAYS);
  const to = addDays(end, MATCH_DAYS);
  // A payment split for a group is matched at its full amount: that is what paid the bill.
  const fronted = new Map(transactions.filter((t) => t.category === FRONTED).map((t) => [t.id.slice(0, -"|fronted".length), t.amount]));
  const candidates = transactions
    .filter((t) => t.amount < 0 && t.category !== FRONTED && t.date >= from && t.date <= to)
    .map((t) => (fronted.has(t.id) ? { ...t, amount: t.amount + fronted.get(t.id)! } : t));
  // Every plausible pairing, closest first, then taken greedily.
  const pairs: { c: Commitment; t: Transaction; gap: number }[] = [];
  for (const c of list) {
    for (const t of candidates) {
      const gap = Math.abs(daysBetween(c.date, t.date));
      if (gap > MATCH_DAYS || Math.abs(-t.amount - c.amount) > c.amount * MATCH_SHARE) continue;
      if (t.category && !related(t.category, c.category)) continue;
      pairs.push({ c, t, gap });
    }
  }
  pairs.sort((a, b) => a.gap - b.gap || Math.abs(-a.t.amount - a.c.amount) - Math.abs(-b.t.amount - b.c.amount));
  const used = new Set<string>();
  for (const { c, t } of pairs) {
    if (c.paidBy || used.has(t.id)) continue;
    c.paidBy = t;
    used.add(t.id);
  }
  return list;
}

/**
 * Commitments still expected: not yet paid, and not long past. One more than
 * a few days overdue with no payment is taken as not happening (or paid in a
 * way that didn't match) and stops counting.
 */
export function stillExpected(list: readonly Commitment[], today: IsoDate): Commitment[] {
  const cutoff = addDays(today, -MATCH_DAYS);
  return list.filter((c) => !c.paidBy && c.date >= cutoff);
}

/** Scheduled amounts per exact category. Roll them up with `rolledUp`. */
export function scheduledByCategory(list: readonly Commitment[]): Map<string, Cents> {
  const totals = new Map<string, Cents>();
  for (const c of list) totals.set(c.category, (totals.get(c.category) ?? 0) + c.amount);
  return totals;
}
